import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import {
  SignOut, User, Compass, IdentificationCard, BookmarkSimple,
  CheckCircle, Gear, List, X, House, CreditCard,
} from "@phosphor-icons/react";

export default function Header() {
  const { user, profile, logout } = useAuth();
  const loc = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  const onLogout = (e) => {
    if (e) { e.preventDefault(); e.stopPropagation(); }
    logout();
    window.location.href = "/";
  };

  const linkCls = (path) =>
    `px-3 py-2 text-xs uppercase tracking-[0.2em] font-semibold transition-colors duration-150 ${
      loc.pathname === path || loc.pathname.startsWith(path + "/")
        ? "text-brand"
        : "text-ink-soft hover:text-ink"
    }`;

  const mobileLinkCls = (path) =>
    `flex items-center gap-3 px-5 py-4 text-sm uppercase tracking-[0.18em] font-semibold border-b border-border transition-colors ${
      loc.pathname === path ? "text-brand bg-brand/5" : "text-ink hover:bg-slate-50"
    }`;

  const displayName = profile?.full_name || user?.full_name || user?.email || "U";
  const initials = displayName.charAt(0).toUpperCase();

  return (
    <header data-testid="app-header" className="sticky top-0 z-50 bg-white border-b border-border">
      <div className="max-w-[1400px] mx-auto px-4 lg:px-10 h-16 flex items-center justify-between">

        {/* Logo */}
        <Link to={user ? "/home" : "/"} className="flex items-center group z-50 relative h-16" data-testid="brand-link">
          <img
            src="/logo.png"
            alt="Opplio"
            className="h-[120px] md:h-[150px] w-auto object-contain select-none pointer-events-none -ml-6"
            onContextMenu={(e) => e.preventDefault()}
            draggable="false"
          />
        </Link>

        {/* Desktop nav */}
        <nav className="hidden md:flex items-center gap-1">
          {user ? (
            <>
              <Link to="/home" className={linkCls("/home")}>
                <span className="inline-flex items-center gap-1.5"><House size={13} weight="bold" />Home</span>
              </Link>
              <Link to="/dashboard" className={linkCls("/dashboard")}>
                <span className="inline-flex items-center gap-1.5"><Compass size={13} weight="bold" />Jobs</span>
              </Link>
              <Link to="/saved" className={linkCls("/saved")}>
                <span className="inline-flex items-center gap-1.5"><BookmarkSimple size={13} weight="bold" />Saved</span>
              </Link>
              <Link to="/applied" className={linkCls("/applied")}>
                <span className="inline-flex items-center gap-1.5"><CheckCircle size={13} weight="bold" />Applied</span>
              </Link>
              <Link to="/profile" className={linkCls("/profile")}>
                <span className="inline-flex items-center gap-1.5"><IdentificationCard size={13} weight="bold" />Profile</span>
              </Link>
              <Link to="/billing" className={linkCls("/billing")}>
                <span className="inline-flex items-center gap-1.5"><CreditCard size={13} weight="bold" />Billing</span>
              </Link>

              {/* User avatar + sign out */}
              <div className="hidden lg:flex items-center gap-2 ml-3 pl-3 border-l border-border">
                <div className="w-7 h-7 bg-ink text-white flex items-center justify-center text-[11px] font-bold flex-shrink-0">
                  {initials}
                </div>
                <span className="text-xs text-ink-soft truncate max-w-[130px]">
                  {profile?.full_name || user?.full_name || user?.email}
                </span>
              </div>
              <Button variant="ghost" size="sm" onClick={onLogout} className="ml-2 uppercase tracking-wider text-xs hidden md:flex text-ink-soft hover:text-ink">
                <SignOut size={13} weight="bold" className="mr-1.5" />Sign out
              </Button>
            </>
          ) : (
            <>
              <Link to="/auth" className={linkCls("/auth")}>Sign in</Link>
              <Link to="/auth?mode=register">
                <Button size="sm" className="bg-brand hover:bg-brand-hover text-white rounded-xl uppercase tracking-wider text-xs h-9 px-4 ml-2">
                  <User size={13} weight="bold" className="mr-1.5" />Get started
                </Button>
              </Link>
            </>
          )}
        </nav>

        {/* Mobile toggle */}
        <button
          className="md:hidden flex items-center justify-center p-2 text-ink z-50 relative"
          onClick={() => setMobileOpen(!mobileOpen)}
          aria-label="Toggle menu"
        >
          {mobileOpen ? <X size={22} weight="bold" /> : <List size={22} weight="bold" />}
        </button>
      </div>

      {/* Mobile menu overlay */}
      {mobileOpen && (
        <div className="md:hidden fixed inset-0 top-[64px] z-40 bg-white border-t border-border overflow-y-auto">
          {user ? (
            <div className="flex flex-col h-full">
              <Link to="/home" onClick={() => setMobileOpen(false)} className={mobileLinkCls("/home")}>
                <House size={16} weight="bold" />Home
              </Link>
              <Link to="/dashboard" onClick={() => setMobileOpen(false)} className={mobileLinkCls("/dashboard")}>
                <Compass size={16} weight="bold" />Jobs
              </Link>
              <Link to="/saved" onClick={() => setMobileOpen(false)} className={mobileLinkCls("/saved")}>
                <BookmarkSimple size={16} weight="bold" />Saved
              </Link>
              <Link to="/applied" onClick={() => setMobileOpen(false)} className={mobileLinkCls("/applied")}>
                <CheckCircle size={16} weight="bold" />Applied
              </Link>
              <Link to="/profile" onClick={() => setMobileOpen(false)} className={mobileLinkCls("/profile")}>
                <IdentificationCard size={16} weight="bold" />Profile
              </Link>
              <Link to="/billing" onClick={() => setMobileOpen(false)} className={mobileLinkCls("/billing")}>
                <CreditCard size={16} weight="bold" />Billing
              </Link>
              <Link to="/settings" onClick={() => setMobileOpen(false)} className={mobileLinkCls("/settings")}>
                <Gear size={16} weight="bold" />Settings
              </Link>

              <div className="mt-auto p-5 border-t border-border bg-slate-50/40">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-ink text-white flex items-center justify-center text-sm font-bold flex-shrink-0">
                    {initials}
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-bold text-ink truncate">{profile?.full_name || user?.full_name || "—"}</p>
                    <p className="text-xs text-ink-soft truncate">{user?.email}</p>
                  </div>
                </div>
                <Button variant="outline" className="w-full justify-center border-slate-200 text-ink uppercase tracking-wider text-xs h-11 rounded-xl" onClick={onLogout}>
                  <SignOut size={15} weight="bold" className="mr-2" />Sign out
                </Button>
              </div>
            </div>
          ) : (
            <div className="p-6 flex flex-col gap-3">
              <Link to="/auth" onClick={() => setMobileOpen(false)} className="block py-4 text-center text-sm uppercase tracking-[0.2em] font-semibold text-ink border border-slate-200 hover:bg-slate-50">
                Sign in
              </Link>
              <Link to="/auth?mode=register" onClick={() => setMobileOpen(false)}>
                <Button className="w-full bg-brand hover:bg-brand-hover text-white rounded-xl uppercase tracking-wider text-sm h-12">
                  <User size={16} weight="bold" className="mr-2" />Get started
                </Button>
              </Link>
            </div>
          )}
        </div>
      )}
    </header>
  );
}
